/**
 * Vérification de la signature Ed25519 dans le navigateur.
 *
 * ## Pourquoi une bibliothèque plutôt que Web Crypto
 *
 * `crypto.subtle` sait faire Ed25519 depuis Chrome 137, Safari 17 et Firefox 130.
 * Or la PWA vise des **Android d'entrée de gamme**, dont le WebView est souvent
 * bien plus ancien. S'appuyer sur Web Crypto reviendrait à ce que la
 * vérification hors ligne — la fonction même de cette application — soit
 * indisponible précisément sur les terminaux du terrain.
 *
 * `@noble/ed25519` est du JavaScript pur, sans dépendance, et fonctionne
 * partout. Un seul chemin de code, pas de détection de fonctionnalité, pas de
 * comportement qui dépend du téléphone de l'agent.
 *
 * L'interopérabilité est garantie par la norme : Ed25519 (RFC 8032) est
 * déterministe, et une signature produite côté serveur par `node:crypto` se
 * vérifie ici à l'identique.
 */

import { verifyAsync } from '@noble/ed25519';

/** Décode une chaîne base64url en octets, sans dépendre de `Buffer`. */
export function depuisBase64Url(valeur: string): Uint8Array | null {
  const base64 = valeur.replace(/-/g, '+').replace(/_/g, '/');
  const complete = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  try {
    const binaire = atob(complete);
    return Uint8Array.from(binaire, (caractere) => caractere.charCodeAt(0));
  } catch {
    return null;
  }
}

/**
 * Vérifie une signature sur la forme canonique **telle qu'elle a été scannée**.
 *
 * Ne lève jamais : sur un pont, une exception non rattrapée laisserait l'agent
 * devant un écran blanc. Un contenu inexploitable est un `false`, c'est-à-dire
 * un document qui n'atteste rien.
 */
export async function verifierSignature(
  canonique: string,
  signature: string,
  clePublique: string,
): Promise<boolean> {
  const octetsSignature = depuisBase64Url(signature);
  const octetsCle = depuisBase64Url(clePublique);

  // 64 octets pour une signature Ed25519, 32 pour une clé : toute autre
  // longueur est un rejet, pas une erreur.
  if (octetsSignature?.length !== 64 || octetsCle?.length !== 32) {
    return false;
  }

  try {
    return await verifyAsync(octetsSignature, new TextEncoder().encode(canonique), octetsCle);
  } catch {
    return false;
  }
}
